import React, { useState } from 'react';
import '../App.css';

function AccuracyTrends({ trends }) {
  const [viewType, setViewType] = useState('year');
  const [showTop30, setShowTop30] = useState(false);

  if (!trends) {
    return <div className="accuracy-trends loading">Loading accuracy trends...</div>;
  }

  const data = viewType === 'year' ? trends.by_year : trends.by_round;

  // Use top 30% accuracy if showTop30 is enabled and data supports it
  const hasTop30Data = data.length > 0 && data[0].top_30_accuracy !== undefined;
  const accuracyKey = (showTop30 && hasTop30Data) ? 'top_30_accuracy' : 'accuracy';
  const totalKey = (showTop30 && hasTop30Data) ? 'top_30_total' : 'total';
  const correctKey = (showTop30 && hasTop30Data) ? 'top_30_correct' : 'correct';

  const maxAccuracy = Math.max(...data.map(d => d[accuracyKey]));
  const minAccuracy = Math.min(...data.map(d => d[accuracyKey]));

  return (
    <div className="accuracy-trends">
      <div className="trends-header">
        <h1>Accuracy Trends</h1>
        <p className="subtitle">Model performance over time</p>

        <div className="view-toggle">
          <button
            className={`toggle-btn ${viewType === 'year' ? 'active' : ''}`}
            onClick={() => setViewType('year')}
          >
            By Year
          </button>
          <button
            className={`toggle-btn ${viewType === 'round' ? 'active' : ''}`}
            onClick={() => setViewType('round')}
          >
            By Round
          </button>
        </div>

        <div className="view-toggle" style={{ marginTop: '1rem' }}>
          <button
            className={`toggle-btn ${!showTop30 ? 'active' : ''}`}
            onClick={() => setShowTop30(false)}
          >
            All Predictions
          </button>
          <button
            className={`toggle-btn ${showTop30 ? 'active' : ''}`}
            onClick={() => setShowTop30(true)}
          >
            Top 30% Confidence
          </button>
        </div>
      </div>

      <div className="trends-summary">
        <div className="summary-card">
          <div className="summary-value">{data.length}</div>
          <div className="summary-label">{viewType === 'year' ? 'Years' : 'Rounds'}</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{maxAccuracy.toFixed(1)}%</div>
          <div className="summary-label">Highest Accuracy</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{minAccuracy.toFixed(1)}%</div>
          <div className="summary-label">Lowest Accuracy</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">
            {(data.reduce((sum, d) => sum + d[accuracyKey], 0) / data.length).toFixed(1)}%
          </div>
          <div className="summary-label">Average Accuracy</div>
        </div>
      </div>

      <div className="chart-container">
        <div className="chart-title">
          {viewType === 'year' ? 'Accuracy by Year' : 'Accuracy by Round Number'}
        </div>
        <div className="bar-chart">
          {data.map((item, index) => {
            // Calculate bar height to fill container properly
            // The tallest bar should reach 95% of container height
            // All other bars scale proportionally based on their accuracy difference
            const maxBarHeight = 95; // Tallest bar at 95% of container
            const minBarHeight = 10;  // Smallest bar at least 10% visible

            const accuracyRange = maxAccuracy - minAccuracy;

            // If all accuracies are the same, show all bars at max height
            let barHeight;
            if (accuracyRange === 0) {
              barHeight = maxBarHeight;
            } else {
              // Scale linearly: smallest bar = minBarHeight, largest bar = maxBarHeight
              const proportion = (item[accuracyKey] - minAccuracy) / accuracyRange;
              barHeight = minBarHeight + (proportion * (maxBarHeight - minBarHeight));
            }

            const avgAccuracy = data.reduce((sum, d) => sum + d[accuracyKey], 0) / data.length;
            const isAboveAverage = item[accuracyKey] >= avgAccuracy;

            return (
              <div key={index} className="bar-wrapper">
                <div className="bar-container">
                  <div
                    className={`bar ${isAboveAverage ? 'above-avg' : 'below-avg'}`}
                    style={{ height: `${barHeight}%` }}
                    title={`${item.label}: ${item[accuracyKey].toFixed(1)}% (${item[correctKey]}/${item[totalKey]})`}
                  >
                    <span className="bar-value">{item[accuracyKey].toFixed(1)}%</span>
                  </div>
                </div>
                <div className="bar-label">{item.label}</div>
                <div className="bar-count">{item[totalKey]}</div>
              </div>
            );
          })}
        </div>

        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color above-avg"></div>
            <span>Above Average</span>
          </div>
          <div className="legend-item">
            <div className="legend-color below-avg"></div>
            <span>Below Average</span>
          </div>
        </div>
      </div>

      <div className="trends-table">
        <h3>Detailed {viewType === 'year' ? 'Year' : 'Round'} Statistics {showTop30 ? '(Top 30% Confidence)' : '(All Predictions)'}</h3>
        <table>
          <thead>
            <tr>
              <th>{viewType === 'year' ? 'Year' : 'Round'}</th>
              <th>Total Matches</th>
              <th>Correct</th>
              <th>Incorrect</th>
              <th>Accuracy</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index}>
                <td className="label-cell">{item.label}</td>
                <td>{item[totalKey]}</td>
                <td className="correct-cell">{item[correctKey]}</td>
                <td className="incorrect-cell">{item[totalKey] - item[correctKey]}</td>
                <td className="accuracy-cell">{item[accuracyKey].toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AccuracyTrends;
